import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AiOutlineClose, AiOutlineMenu } from 'react-icons/ai';
import { FaChevronDown, FaUserCircle, FaTachometerAlt, FaClock, FaSignOutAlt, FaBriefcase, FaClipboardCheck, FaUsers, FaProjectDiagram, FaStar, FaCalendarAlt, FaChartBar, FaCog, FaBuilding, FaDollarSign, FaFileInvoiceDollar, FaShieldAlt, FaMagic, FaComments, FaCommentSlash } from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi2';
import { MdFaceRetouchingNatural } from 'react-icons/md';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from '../components/Logo';
import api, { getServerMode } from '../api';
import AISidebar from '../components/AISidebar';

const navItems = [
  { id: '', text: 'Home' },
  { id: 'about', text: 'About' },
  { id: 'services', text: 'Services' },
  { id: 'careers', text: 'Careers' },
  { id: 'insights', text: 'Insights' },
  { id: 'contact', text: 'Contact' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyMsg, setPasskeyMsg] = useState('');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const passkeySupported = api.passkeySupported();

  const isActive = (id) => {
    const path = '/' + id;
    return location.pathname === path || (id === '' && location.pathname === '/');
  };

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
    setMobileOpen(false);
    navigate('/');
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close AI sidebar when session expires or user logs out
  useEffect(() => {
    if (!user) setAiOpen(false);
  }, [user]);

  // Know up front whether this employee already has a Face ID set up, so
  // the dropdown action can say "Set up" vs "Registered".
  useEffect(() => {
    if (!user || !passkeySupported) { setHasPasskey(false); return; }
    api.getMyPasskeys().then(rows => setHasPasskey(rows.length > 0)).catch(() => {});
  }, [user, passkeySupported]);

  const handleSetupPasskey = async () => {
    setPasskeyBusy(true);
    setPasskeyMsg('');
    try {
      await api.registerPasskey('Face ID');
      setHasPasskey(true);
      setPasskeyMsg('Face ID set up!');
    } catch (err) {
      setPasskeyMsg(err.message || 'Could not set up Face ID.');
    }
    setPasskeyBusy(false);
  };

  return (
    <>
      <nav className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center h-28">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <Logo size={96} showSubtitle={false} className="hidden sm:inline-flex" />
            <Logo size={96} showSubtitle={false} className="sm:hidden" />
          </Link>

          {/* Desktop Nav Links */}
          <ul className="hidden lg:flex items-center gap-0.5">
            {navItems.map(item => (
              <li key={item.id}>
                <Link
                  to={`/${item.id}`}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.id)
                      ? 'text-[#0c1b4d] bg-blue-50'
                      : 'text-gray-600 hover:text-[#0c1b4d] hover:bg-gray-50'
                  }`}
                >
                  {item.text}
                </Link>
              </li>
            ))}
          </ul>

          {/* Right: Auth */}
          <div className="hidden lg:flex items-center gap-2">
            {user && (
              <button
                onClick={() => setAiOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#0c1b4d] text-[#D4AF37] rounded-lg text-sm font-semibold hover:bg-blue-900 transition-colors shadow-sm"
                title="Marvy AI"
              >
                <HiSparkles size={15} />
                <span>Marvy AI</span>
              </button>
            )}
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-3 py-2 bg-[#0c1b4d] text-white rounded-lg text-sm font-medium hover:bg-blue-900 transition-colors"
                >
                  <FaUserCircle size={15} />
                  <span>{user.name.split(' ')[0]}</span>
                  {user.permissions?.includes('admin_access') && (
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${getServerMode() === 'cloud' ? 'bg-green-400' : 'bg-yellow-400'}`}
                      title={getServerMode() === 'cloud' ? 'Cloudflare' : 'Oracle Cloud'}
                    />
                  )}
                  <FaChevronDown
                    size={11}
                    className={`transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50">
                    <div className="px-4 py-2.5 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-800">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">ID: {user.employeeId}</p>
                    </div>
                    <Link
                      to="/employee"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors"
                    >
                      <FaTachometerAlt size={13} className="text-gray-400" /> Dashboard
                    </Link>
                    {user.permissions?.includes('admin_access') && (
                      <>
                        <Link to="/admin/timesheets" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                          <FaClipboardCheck size={13} className="text-gray-400" /> Review Timesheets
                        </Link>
                        <Link to="/admin/employees" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                          <FaUsers size={13} className="text-gray-400" /> Manage Employees
                        </Link>
                        <Link to="/admin/projects" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                          <FaProjectDiagram size={13} className="text-gray-400" /> Manage Projects
                        </Link>
                        <Link to="/admin/holidays" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                          <FaStar size={13} className="text-gray-400" /> Company Holidays
                        </Link>
                        <Link to="/admin/positions" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                          <FaBriefcase size={13} className="text-gray-400" /> Manage Positions
                        </Link>
                        <Link to="/admin/leaves" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                          <FaCalendarAlt size={13} className="text-gray-400" /> Leave Requests
                        </Link>
                        <Link to="/admin/reports" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                          <FaChartBar size={13} className="text-gray-400" /> Reports &amp; Analytics
                        </Link>
                        <Link to="/admin/resume-booster" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                          <FaMagic size={13} className="text-gray-400" /> Resume Booster
                        </Link>
                        {user.permissions?.includes('manage_clients') && (
                          <Link to="/admin/clients" onClick={() => setDropdownOpen(false)}
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                            <FaBuilding size={13} className="text-gray-400" /> Clients
                          </Link>
                        )}
                        {user.permissions?.includes('manage_consultant_rates') && (
                          <Link to="/admin/consultant-rates" onClick={() => setDropdownOpen(false)}
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                            <FaDollarSign size={13} className="text-gray-400" /> Consultant Rates
                          </Link>
                        )}
                        {user.permissions?.includes('generate_invoices') && (
                          <Link to="/admin/invoices" onClick={() => setDropdownOpen(false)}
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                            <FaFileInvoiceDollar size={13} className="text-gray-400" /> Invoices
                          </Link>
                        )}
                        <Link to="/admin/settings" onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                          <FaCog size={13} className="text-gray-400" /> Settings
                        </Link>
                        {user.permissions?.includes('manage_roles') && (
                          <Link to="/admin/roles" onClick={() => setDropdownOpen(false)}
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                            <FaShieldAlt size={13} className="text-gray-400" /> Roles &amp; Permissions
                          </Link>
                        )}
                        {user.permissions?.includes('manage_chat_access') && (
                          <Link to="/admin/chat-permissions" onClick={() => setDropdownOpen(false)}
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                            <FaCommentSlash size={13} className="text-gray-400" /> Chat Permissions
                          </Link>
                        )}
                        {user.permissions?.includes('manage_passkeys') && (
                          <Link to="/admin/passkeys" onClick={() => setDropdownOpen(false)}
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors">
                            <MdFaceRetouchingNatural size={13} className="text-gray-400" /> Face ID / Passkeys
                          </Link>
                        )}
                      </>
                    )}
                    {passkeySupported && (
                      <div className="px-4 py-2.5">
                        <button
                          onClick={handleSetupPasskey}
                          disabled={passkeyBusy || hasPasskey}
                          className="flex items-center gap-2.5 w-full text-left text-sm text-gray-700 hover:text-[#0c1b4d] transition-colors disabled:cursor-default disabled:hover:text-gray-700"
                        >
                          <MdFaceRetouchingNatural size={13} className={hasPasskey ? 'text-green-500' : 'text-gray-400'} />
                          {hasPasskey ? 'Face ID registered ✓' : (passkeyBusy ? 'Setting up…' : 'Set up Face ID')}
                        </button>
                        {passkeyMsg && <p className="text-xs text-gray-400 mt-1">{passkeyMsg}</p>}
                      </div>
                    )}
                    {user.permissions?.includes('chat_access') && (
                      <Link
                        to="/chat"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors"
                      >
                        <FaComments size={13} className="text-gray-400" /> Messages
                      </Link>
                    )}
                    <Link
                      to="/employee/timesheet"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors"
                    >
                      <FaClock size={13} className="text-gray-400" /> Submit Timesheet
                    </Link>
                    <Link
                      to="/employee/leaves"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0c1b4d] transition-colors"
                    >
                      <FaCalendarAlt size={13} className="text-gray-400" /> Request Time Off
                    </Link>
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <FaSignOutAlt size={13} /> Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 bg-[#0c1b4d] text-white rounded-lg text-sm font-semibold hover:bg-blue-900 transition-colors shadow-sm"
              >
                Employee Login
              </Link>
            )}
          </div>

          {/* Mobile: Marvy AI + Toggle */}
          <div className="lg:hidden flex items-center gap-2">
            {user && (
              <button
                onClick={() => setAiOpen(true)}
                className="flex items-center justify-center w-9 h-9 bg-[#0c1b4d] text-[#D4AF37] rounded-lg hover:bg-blue-900 transition-colors shadow-sm"
                title="Marvy AI"
              >
                <HiSparkles size={18} />
              </button>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 text-gray-600 hover:text-[#0c1b4d] hover:bg-gray-100 rounded-lg transition-colors"
            >
              {mobileOpen ? <AiOutlineClose size={21} /> : <AiOutlineMenu size={21} />}
            </button>
          </div>
        </div>
      </nav>

      <AISidebar open={aiOpen} onClose={() => setAiOpen(false)} />

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 top-28 z-40 bg-black bg-opacity-40"
          onClick={() => setMobileOpen(false)}
        >
          <div
            className="bg-white w-72 h-full shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-1 overflow-y-auto p-3">
              <ul className="space-y-0.5">
                {navItems.map(item => (
                  <li key={item.id}>
                    <Link
                      to={`/${item.id}`}
                      className={`block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive(item.id)
                          ? 'bg-blue-50 text-[#0c1b4d]'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-[#0c1b4d]'
                      }`}
                    >
                      {item.text}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-4 pt-4 border-t border-gray-100">
                {user ? (
                  <div>
                    <div className="px-3 py-2 mb-2">
                      <p className="text-sm font-semibold text-gray-800">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.title}</p>
                    </div>
                    <Link to="/employee" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                      <FaTachometerAlt size={13} /> Dashboard
                    </Link>
                    {user.permissions?.includes('admin_access') && (
                      <>
                        <Link to="/admin/timesheets" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                          <FaClipboardCheck size={13} /> Review Timesheets
                        </Link>
                        <Link to="/admin/employees" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                          <FaUsers size={13} /> Manage Employees
                        </Link>
                        <Link to="/admin/projects" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                          <FaProjectDiagram size={13} /> Manage Projects
                        </Link>
                        <Link to="/admin/holidays" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                          <FaStar size={13} /> Company Holidays
                        </Link>
                        <Link to="/admin/positions" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                          <FaBriefcase size={13} /> Manage Positions
                        </Link>
                        <Link to="/admin/leaves" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                          <FaCalendarAlt size={13} /> Leave Requests
                        </Link>
                        <Link to="/admin/reports" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                          <FaChartBar size={13} /> Reports &amp; Analytics
                        </Link>
                        <Link to="/admin/resume-booster" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                          <FaMagic size={13} /> Resume Booster
                        </Link>
                        {user.permissions?.includes('manage_clients') && (
                          <Link to="/admin/clients" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                            <FaBuilding size={13} /> Clients
                          </Link>
                        )}
                        {user.permissions?.includes('manage_consultant_rates') && (
                          <Link to="/admin/consultant-rates" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                            <FaDollarSign size={13} /> Consultant Rates
                          </Link>
                        )}
                        {user.permissions?.includes('generate_invoices') && (
                          <Link to="/admin/invoices" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                            <FaFileInvoiceDollar size={13} /> Invoices
                          </Link>
                        )}
                        <Link to="/admin/settings" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                          <FaCog size={13} /> Settings
                        </Link>
                        {user.permissions?.includes('manage_roles') && (
                          <Link to="/admin/roles" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                            <FaShieldAlt size={13} /> Roles &amp; Permissions
                          </Link>
                        )}
                        {user.permissions?.includes('manage_chat_access') && (
                          <Link to="/admin/chat-permissions" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                            <FaCommentSlash size={13} /> Chat Permissions
                          </Link>
                        )}
                        {user.permissions?.includes('manage_passkeys') && (
                          <Link to="/admin/passkeys" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                            <MdFaceRetouchingNatural size={13} /> Face ID / Passkeys
                          </Link>
                        )}
                      </>
                    )}
                    {passkeySupported && (
                      <div className="px-3 py-2.5">
                        <button
                          onClick={handleSetupPasskey}
                          disabled={passkeyBusy || hasPasskey}
                          className="flex items-center gap-2 w-full text-left text-sm text-gray-700 disabled:cursor-default"
                        >
                          <MdFaceRetouchingNatural size={13} className={hasPasskey ? 'text-green-500' : ''} />
                          {hasPasskey ? 'Face ID registered ✓' : (passkeyBusy ? 'Setting up…' : 'Set up Face ID')}
                        </button>
                        {passkeyMsg && <p className="text-xs text-gray-400 mt-1">{passkeyMsg}</p>}
                      </div>
                    )}
                    {user.permissions?.includes('chat_access') && (
                      <Link to="/chat" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                        <FaComments size={13} /> Messages
                      </Link>
                    )}
                    <Link to="/employee/timesheet" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                      <FaClock size={13} /> Submit Timesheet
                    </Link>
                    <Link to="/employee/leaves" className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg">
                      <FaCalendarAlt size={13} /> Request Time Off
                    </Link>
                    <button
                      onClick={() => { setMobileOpen(false); setAiOpen(true); }}
                      className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-semibold text-[#D4AF37] bg-[#0c1b4d] hover:bg-blue-900 rounded-lg mt-2 transition-colors"
                    >
                      <HiSparkles size={13} /> Marvy AI
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg mt-2"
                    >
                      <FaSignOutAlt size={13} /> Sign Out
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="block text-center px-4 py-2.5 bg-[#0c1b4d] text-white rounded-lg text-sm font-semibold hover:bg-blue-900 transition-colors"
                  >
                    Employee Login
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
